import { useMemo, useState } from 'react';
import { ExpandedProduct, GroupedProduct, OfferCodeSku, Sku, Variant } from '@v2/shared/lib/types';
import applyOfferCodeSkus from '@v2/shared/lib/applyOfferCodeSkus';
import { useCart } from '@v2/shared/lib/cart';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { buildVariantSelectors, findSkuByVariants } from './variants';
import { trackAddToCart, trackViewItem } from '@v2/shared/lib/analytics';

type Options = {
  product: ExpandedProduct;
  offerCodeSkus: OfferCodeSku[];
  offerCode: string | null;
  selectedSkuCode: string | null;
};

/**
 * The state of the buy column: which format is chosen, what it costs, and what happens
 * when one of the three buttons is pressed.
 *
 * Note what this does *not* do. headless's equivalent opened with a refetch of the
 * product it had already been given, because its pages are statically generated and can
 * be served from an hour-old snapshot. This page is rendered per request, so the props
 * are already current — there is one product here, the one the props builder resolved,
 * and it does not change after mount.
 *
 * The offer's prices are overlaid here rather than server-side for the reason
 * `applyOfferCodeSkus` gives: one copy of that join, and it is the one that renders.
 */
export default function useProductPage({ product, offerCodeSkus, offerCode, selectedSkuCode }: Options) {
  const { addItem } = useCart();
  const { buildUrl } = useUrlGenerator();
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * More than one grouped product means the other formats are separate rows, each with
   * its own skus; one (or none) means this product carries them all itself. The `> 1`
   * rather than `>= 1` is the source's, and it matters: a product in a group of one is
   * grouped with nothing.
   */
  const hasGroupedProducts = (product.grouped_products?.length ?? 0) > 1;

  /**
   * Every sku the chooser can reach, with the offer's prices already on them. Formats
   * that are no longer sellable are dropped rather than shown disabled — headless did
   * the same, and an option that cannot be bought is not an option.
   */
  const activeSkus = useMemo(() => {
    const skus = hasGroupedProducts
      ? (product.grouped_products ?? []).filter((grouped) => grouped.buyable).flatMap((grouped) => grouped.active_skus)
      : product.active_skus ?? [];

    return applyOfferCodeSkus(skus, offerCodeSkus);
  }, [product, hasGroupedProducts, offerCodeSkus]);

  /**
   * Which product owns each sku, so an option can link to the right page. When the
   * formats are not grouped, that is always this product.
   */
  const skuProducts = useMemo(() => {
    const mapping = new Map<number, GroupedProduct | ExpandedProduct>();

    activeSkus.forEach((sku) => {
      mapping.set(
        sku.id,
        (hasGroupedProducts
          ? product.grouped_products?.find((grouped) =>
              grouped.active_skus.some((groupedSku) => groupedSku.code === sku.code)
            )
          : product) || product
      );
    });

    return mapping;
  }, [activeSkus, hasGroupedProducts, product]);

  /**
   * The url's sku when it named one, the base sku otherwise.
   *
   * Taken from `activeSkus` rather than from the product, so the starting price already
   * has the offer on it and does not drop the moment the visitor picks a format.
   *
   * Undefined is a real state, not a defensive `?.`: `Product::getBaseSku()` returns
   * null for a product whose skus have all been deactivated, and JMS omits the key
   * rather than serializing it — so a page whose whole buy column is the "no longer
   * available" alert arrives with nothing to select. Dereferencing it here would throw
   * inside this initialiser, which fails the server render and then throws again on
   * hydration: a blank page that answers 200.
   */
  const [chosenSku, setChosenSku] = useState<Sku | undefined>(
    () =>
      (selectedSkuCode ? activeSkus.find((sku) => sku.code === selectedSkuCode) : undefined) ||
      (product.base_sku && activeSkus.find((sku) => sku.code === product.base_sku.code)) ||
      (product.base_sku ? applyOfferCodeSkus([product.base_sku], offerCodeSkus)[0] : undefined)
  );

  /**
   * The chosen format at the prices in force now rather than the ones it was chosen at:
   * the offer can arrive after mount, once the cart has loaded — see useStickyOffer.
   */
  const selectedSku = (chosenSku && activeSkus.find((sku) => sku.code === chosenSku.code)) || chosenSku;

  const variantSelectors = useMemo(
    () => (selectedSku ? buildVariantSelectors(activeSkus, selectedSku) : []),
    [activeSkus, selectedSku]
  );

  /** Only worth drawing a chooser when something is actually being chosen between. */
  const hasVariants = variantSelectors.some((selector) => selector.options.length > 1);

  /**
   * Picking an option navigates when it belongs to another product and switches in
   * place when it does not — which is why the options are anchors with an onClick
   * rather than buttons. Same product: swap the sku and swallow the navigation. Other
   * product: let the browser follow the link, because that page is a different row with
   * a different cover, description and structured data.
   */
  function selectVariant(variant: Variant, event?: { preventDefault: () => void }) {
    const sku = selectedSku && findSkuByVariants(activeSkus, selectedSku.variants, variant);

    if (!sku) {
      return;
    }

    if (skuProducts.get(sku.id)?.id === product.id) {
      event?.preventDefault();
      setChosenSku(sku);
    }
  }

  /**
   * Adds the chosen format and leaves for the cart — or, with a wallet payload, for
   * checkout, because the payload *is* the order and there is nothing left to confirm.
   *
   * A full navigation rather than a router push: there is no router here, and both
   * destinations are legacy pages that render server-side regardless.
   */
  async function addToCart(paymentPayload: string | null = null) {
    if (!selectedSku) {
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      const cart = await addItem(selectedSku.code, offerCode, paymentPayload);
      const added = cart.items?.[cart.items.length - 1];

      /*
       * The GA4 add, from the line the server actually wrote rather than from the sku this
       * page asked for, so the product and the price reported are the ones in the cart.
       *
       * Nothing downstream reports it otherwise. The legacy cart page fires `add_to_cart`
       * for any line flagged `recentlyAdded`, which is why it looks as though landing on
       * /cart would cover this — it does not for a line added through the storefront api:
       * driven on the stack, an api add followed by /cart renders the line and no
       * `.recently-added` marker, and the page's only gtag event is the cart upsell's
       * `view_item_list`.
       */
      if (added) {
        trackAddToCart(added);
      }

      if (paymentPayload && cart.checkout_url) {
        // xPayload=Y is what tells checkout to charge the payload already on the order
        // rather than asking for a card.
        window.location.href = `${cart.checkout_url}&xPayload=Y`;
      } else {
        window.location.href = buildUrl('/cart');
      }
    } catch (failure) {
      console.error('Could not add the product to the cart', failure);
      // Only on the failure path: on the success path the navigation is already under
      // way, and clearing it would flash the button back to its resting state while the
      // visitor waits for the cart.
      setIsAdding(false);
      setError(`${failure}`);
    }
  }

  /**
   * The GA4 view_item push, ported from headless_jshop's useProductDetailPage so a book
   * page feeds the same ecommerce event a magazine PDP already does.
   *
   * The payload is `trackViewItem`'s now rather than this file's — it was the first of
   * these events and had nowhere else to live. What it names a product by has not changed:
   * downstream — GA4, and through it Pinterest, Criteo and Google — matches on the
   * catalog's hex offer_id, which is `base_sku.code`, never the ISBN and never the
   * internal numeric id. The category and the visitor's offer code are this page's to add;
   * nothing else has them.
   */
  function dispatchProductViewEvent() {
    trackViewItem(product, { coupon: offerCode, category: product.category?.name });
  }

  return {
    activeSkus,
    skuProducts,
    selectedSku,
    variantSelectors,
    hasVariants,
    selectVariant,
    addToCart,
    isAdding,
    error,
    dispatchProductViewEvent,
  };
}
